import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    RefreshCw, Info, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp,
    Gauge, Users, CalendarCheck, Database,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getCrmUsage, getCrmUsageDetail } from '../../../api/kpi';
import { PeriodSelector, useAnalyticsPeriod } from './AnalyticsPeriod';
import CrmDetailModal from './CrmDetailModal';
import {
    CLR, CONFIDENCE, ENGAGEMENT, TEAL, fmtNumber, initials,
    pillPalette, relativeDay, scoreTier,
} from './crmAnalyticsUi';

/**
 * Analytics > CRM Analytics > Agent Usage.
 *
 * Answers the question the Agents tab cannot: is each agent actually USING the
 * CRM, and in which parts of it? Three sections:
 *
 *   1. A sortable table — utilisation score, engagement status, active days,
 *      actions, database coverage, modules used.
 *   2. An expandable score breakdown per agent, showing every sub-metric's
 *      contribution and, importantly, which ones could NOT be measured.
 *   3. The Agent x Feature adoption matrix — one cell per module, click to see
 *      the underlying activity.
 *
 * Two deliberate choices about the score:
 *
 *   - The table sorts by ACTIONS by default, not by score. A /100 that ranks
 *     named people is easy to over-read; the raw volume is the honest headline
 *     and the score is there to be interrogated.
 *   - Every row carries a confidence flag, and the breakdown marks unmeasurable
 *     sub-metrics explicitly, so a low score can always be explained rather
 *     than just asserted.
 */

const COLUMNS = [
    { key: 'utilisation_score', label: 'Score', type: 'score', width: 78 },
    { key: 'engagement_status', label: 'Status', type: 'status', width: 104 },
    { key: 'active_days', label: 'Active days', type: 'activeDays' },
    { key: 'last_activity', label: 'Last active', type: 'recency' },
    { key: 'total_actions', label: 'Actions', type: 'num', drill: 'crm_actions' },
    { key: 'actions_per_day', label: 'Per day', type: 'decimal' },
    { key: 'contacts_touched', label: 'Contacts touched', type: 'num', drill: 'contacts_touched' },
    { key: 'database_coverage_pct', label: 'Coverage', type: 'pct', pill: [40, 20], denom: 'assigned_contacts' },
    { key: 'modules_used', label: 'Modules', type: 'modules' },
    { key: 'logins', label: 'Logins', type: 'num', drill: 'logins' },
];

const th = {
    background: CLR.white, color: CLR.black, fontWeight: 700, fontSize: 12.5,
    padding: '8px 9px', borderBottom: `2px solid ${CLR.border}`, textAlign: 'right',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
};
const td = {
    padding: '7px 9px', textAlign: 'right', borderBottom: `1px solid ${CLR.border}`,
    whiteSpace: 'nowrap', color: CLR.black,
};
const stickyAgent = {
    position: 'sticky', left: 0, borderRight: `1px solid ${CLR.border}`, textAlign: 'left',
};

const StatCard = ({ icon, label, value, sub }) => (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-1.5">
            <span
                className="flex items-center justify-center w-7 h-7 rounded-lg"
                style={{ background: 'var(--c-panel-brand-strong)', color: CLR.aquaDark }}
            >
                {icon}
            </span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

const ScoreDial = ({ score }) => {
    const { fg, bg } = pillPalette[scoreTier(score)];
    return (
        <span
            className="inline-flex items-center justify-center rounded-lg font-bold tabular-nums"
            style={{ background: bg, color: fg, minWidth: 42, padding: '3px 8px', fontSize: 13 }}
        >
            {score}
        </span>
    );
};

const StatusPill = ({ status }) => {
    const s = ENGAGEMENT[status] || ENGAGEMENT.inactive;
    return (
        <span
            className="inline-flex items-center gap-1.5 rounded-full"
            style={{ background: s.bg, color: s.fg, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}
        >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot }} />
            {s.label}
        </span>
    );
};

/** Horizontal bar for one score pillar. */
const PillarBar = ({ pillar }) => {
    // A pillar is worth more than its nominal weight when another pillar could
    // not be measured and its points were shared out, so the denominator shown
    // is the effective one — otherwise this reads "20.3 / 20".
    const outOf = pillar.effective_weight ?? pillar.weight;
    const pct = outOf ? Math.round((pillar.points / outOf) * 100) : 0;
    const { fg } = pillPalette[scoreTier(pct)];
    return (
        <div className="mb-2.5">
            <div className="flex items-baseline justify-between mb-1">
                <span className="text-[11px] font-semibold text-gray-700">{pillar.label}</span>
                <span className="text-[11px] tabular-nums text-gray-500">
                    {pillar.available
                        ? <>{pillar.points.toFixed(1)} / {outOf}</>
                        : <span title="No data for this area in this period — its points were shared across the others">not measurable</span>}
                </span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: CLR.grayLight }}>
                <div style={{ width: `${Math.min(pct, 100)}%`, background: fg, height: '100%' }} />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {pillar.metrics.map((m) => (
                    <span
                        key={m.key}
                        className="text-[10.5px]"
                        style={{ color: m.available ? CLR.gray : '#b0b2b4' }}
                        title={m.available
                            ? `${m.points.toFixed(1)} of ${m.max_points} points`
                            : 'Not measurable for this period — its points were shared across the others'}
                    >
                        {m.label}
                        {m.available
                            ? <> · <strong style={{ color: CLR.black }}>{Math.round(m.value * 100)}%</strong></>
                            : ' · n/a'}
                    </span>
                ))}
            </div>
        </div>
    );
};

/**
 * `reloadToken` — bump it from the parent to force a refetch. Used after the
 * Analytics Exclusions modal changes who is in the report; without it the table
 * keeps showing an account that was just excluded until the period is changed.
 */
const AgentUsagePanel = ({ reloadToken = 0 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [helpOpen, setHelpOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    // Volume first, score second — see the component docstring.
    const [sort, setSort] = useState({ key: 'total_actions', dir: 'desc' });
    const [detail, setDetail] = useState({ open: false, loading: false, title: '', rows: [] });
    const loadTokenRef = useRef(0);

    const period = useAnalyticsPeriod();
    const { range } = period;

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        getCrmUsage({ start_date: range.start, end_date: range.end, include: 'matrix' })
            .then((res) => { if (myToken === loadTokenRef.current) setData(res.data); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('CRM usage load failed:', err);
                toast.error('Failed to load CRM usage');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [range.start, range.end, reloadToken]);

    const agents = useMemo(() => {
        const list = [...(data?.agents || [])];
        const { key, dir } = sort;
        list.sort((a, b) => {
            const av = a[key] ?? 0;
            const bv = b[key] ?? 0;
            if (typeof av === 'string' || typeof bv === 'string') {
                return dir === 'asc'
                    ? String(av).localeCompare(String(bv))
                    : String(bv).localeCompare(String(av));
            }
            return dir === 'asc' ? av - bv : bv - av;
        });
        return list;
    }, [data, sort]);

    const totals = data?.totals || {};
    const meta = data?.meta || {};
    const modules = meta.modules || [];

    const toggleSort = (key) =>
        setSort((s) => (s.key === key
            ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'desc' }));

    const runDetail = (params, title) => {
        setDetail({ open: true, loading: true, title, rows: [] });
        getCrmUsageDetail({ ...params, start_date: range.start, end_date: range.end })
            .then((res) => setDetail((d) => ({
                ...d,
                loading: false,
                rows: res.data?.results || [],
                total: res.data?.total,
                truncated: res.data?.truncated,
            })))
            .catch((err) => {
                console.error('CRM usage detail load failed:', err);
                toast.error('Failed to load records');
                setDetail((d) => ({ ...d, loading: false, rows: [] }));
            });
    };

    const SortIcon = ({ colKey }) => {
        if (sort.key !== colKey) return <ArrowUpDown className="w-3 h-3 inline-block opacity-40" />;
        return sort.dir === 'asc'
            ? <ArrowUp className="w-3 h-3 inline-block" style={{ color: TEAL }} />
            : <ArrowDown className="w-3 h-3 inline-block" style={{ color: TEAL }} />;
    };

    const renderCell = (row, col) => {
        const raw = row[col.key];

        if (col.type === 'score') return <ScoreDial score={raw || 0} />;
        if (col.type === 'status') return <StatusPill status={raw} />;

        if (col.type === 'recency') {
            return (
                <span style={{ color: raw ? CLR.black : CLR.gray }}>{relativeDay(raw)}</span>
            );
        }

        if (col.type === 'activeDays') {
            return (
                <span className="tabular-nums">
                    {raw || 0}
                    <span style={{ color: CLR.gray }}> / {row.working_days}</span>
                </span>
            );
        }

        if (col.type === 'modules') {
            return (
                <span className="tabular-nums">
                    {raw || 0}
                    <span style={{ color: CLR.gray }}> / {modules.length || 10}</span>
                </span>
            );
        }

        if (col.type === 'pct') {
            // No assigned database means there is nothing to cover — showing 0%
            // would read as a failure rather than "not applicable".
            if (col.denom && !Number(row[col.denom] || 0)) {
                return <span style={{ color: CLR.gray }}>—</span>;
            }
            const v = Number(raw || 0);
            const { bg, fg } = pillPalette[v >= col.pill[0] ? 'good' : v >= col.pill[1] ? 'mid' : 'bad'];
            return (
                <span style={{
                    display: 'inline-block', padding: '2px 7px', borderRadius: 20,
                    fontSize: 11, fontWeight: 600, background: bg, color: fg,
                }}>
                    {v}%
                </span>
            );
        }

        const text = col.type === 'decimal'
            ? (Number(raw) || 0).toFixed(1)
            : fmtNumber(raw);

        if (col.drill && Number(raw) > 0) {
            return (
                <button
                    onClick={() => runDetail(
                        { agent: row.agent_id, metric: col.drill },
                        `${row.agent_name} · ${col.label}`,
                    )}
                    className="tabular-nums hover:underline"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: CLR.black, font: 'inherit' }}
                    title="View records"
                >
                    {text}
                </button>
            );
        }
        return <span className="tabular-nums" style={{ color: Number(raw) ? CLR.black : CLR.gray }}>{text}</span>;
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-24 text-gray-500 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading CRM usage…
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <PeriodSelector period={period} />

            {/* What is and isn't measured. Shown up front rather than buried,
                because a utilisation score people are ranked on has to be
                auditable by the people being ranked. */}
            <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                className="w-full text-left rounded-lg px-3 py-2.5 flex gap-2 items-start border transition-colors hover:bg-gray-50"
                style={{ background: 'var(--c-panel-brand)', borderColor: 'var(--c-line-brand)' }}
            >
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: CLR.aquaDark }} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold" style={{ color: CLR.aquaDark }}>
                            How the CRM Utilisation Score works
                        </span>
                        {helpOpen
                            ? <ChevronUp className="w-4 h-4" style={{ color: CLR.aquaDark }} />
                            : <ChevronDown className="w-4 h-4" style={{ color: CLR.aquaDark }} />}
                    </div>
                    {helpOpen && (
                        <div className="mt-2 text-[12px] leading-relaxed" style={{ color: CLR.gray }}>
                            <p className="mb-2">
                                Each agent is scored out of 100 across five areas:
                                <strong> CRM usage (20)</strong>, <strong>prospecting (25)</strong>,
                                <strong> pipeline management (20)</strong>, <strong>buyer management (15)</strong> and
                                <strong> sales workflow (20)</strong>. Expand any row to see every
                                sub-metric and what it contributed.
                            </p>
                            <p className="mb-2">
                                <strong>An <em>active day</em> means a meaningful action</strong> — a call, note,
                                SMS, email, lead update, inspection or appraisal. Simply logging in
                                does not make a day active.
                            </p>
                            <p className="mb-2">
                                <strong>Database coverage</strong> is unique contacts the agent touched
                                divided by the contacts assigned to them, so it measures whether the
                                database they were given is actually being worked.
                            </p>
                            <p className="mb-2">
                                <strong>Where a sub-metric cannot be measured</strong> for the selected
                                period it is shown as <em>n/a</em> and its points are shared across the
                                others, so nobody is penalised for data the CRM does not hold. The
                                confidence flag on each row shows how much of that agent's score was
                                directly measured.
                            </p>
                            {(meta.caveats || []).map((c, i) => (
                                <p key={i} className="mb-1">• {c}</p>
                            ))}
                            {meta.events_since && (
                                <p className="mb-1">
                                    • Lead stage tracking is available from {meta.events_since.slice(0, 10)}.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </button>

            {/* Agency summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                    icon={<Users className="w-4 h-4" />}
                    label="Active agents"
                    value={`${totals.active_agents || 0} / ${totals.agents || 0}`}
                    sub={`${totals.adoption_pct || 0}% adoption`}
                />
                <StatCard
                    icon={<Gauge className="w-4 h-4" />}
                    label="Avg score"
                    value={`${totals.avg_utilisation_score || 0}`}
                    sub={`${totals.avg_modules_used || 0} of ${modules.length || 10} modules used`}
                />
                <StatCard
                    icon={<CalendarCheck className="w-4 h-4" />}
                    label="CRM actions"
                    value={fmtNumber(totals.total_actions)}
                    sub={`${fmtNumber(totals.actions_per_agent)} per agent · ${totals.avg_active_days || 0} avg active days`}
                />
                <StatCard
                    icon={<Database className="w-4 h-4" />}
                    label="Database coverage"
                    value={`${totals.database_coverage_pct || 0}%`}
                    sub={`${fmtNumber(totals.contacts_touched)} of ${fmtNumber(totals.assigned_contacts)} contacts`}
                />
            </div>

            {/* Utilisation table */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th
                                    style={{ ...th, ...stickyAgent, zIndex: 2, background: CLR.white }}
                                    onClick={() => toggleSort('agent_name')}
                                >
                                    Agent <SortIcon colKey="agent_name" />
                                </th>
                                {COLUMNS.map((c) => (
                                    <th key={c.key} style={{ ...th, width: c.width }} onClick={() => toggleSort(c.key)}>
                                        {c.label} <SortIcon colKey={c.key} />
                                    </th>
                                ))}
                                <th style={{ ...th, width: 34, cursor: 'default' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {agents.length === 0 && (
                                <tr>
                                    <td colSpan={COLUMNS.length + 2} className="text-center text-sm text-gray-500 py-10">
                                        No agent activity in this range yet. Try a wider date range.
                                    </td>
                                </tr>
                            )}
                            {agents.map((row) => {
                                const isOpen = expanded === row.agent_id;
                                const conf = CONFIDENCE[row.score_confidence] || CONFIDENCE.low;
                                return (
                                    <React.Fragment key={row.agent_id}>
                                        <tr className="hover:bg-gray-50">
                                            <td style={{ ...td, ...stickyAgent, background: CLR.white }}>
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0"
                                                        style={{ width: 26, height: 26, background: CLR.grayLight, color: CLR.gray }}
                                                    >
                                                        {initials(row.agent_name)}
                                                    </span>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold truncate">{row.agent_name}</div>
                                                        <div className="text-[10.5px]" style={{ color: conf.fg }}>
                                                            {conf.label}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {COLUMNS.map((c) => (
                                                <td key={c.key} style={td}>{renderCell(row, c)}</td>
                                            ))}
                                            <td style={{ ...td, textAlign: 'center' }}>
                                                <button
                                                    onClick={() => setExpanded(isOpen ? null : row.agent_id)}
                                                    className="text-gray-400 hover:text-gray-700"
                                                    title={isOpen ? 'Hide score breakdown' : 'Show score breakdown'}
                                                >
                                                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={COLUMNS.length + 2} style={{ background: 'var(--c-panel-subtle)', padding: '12px 14px', borderBottom: `1px solid ${CLR.border}` }}>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                                                        {(row.score_breakdown || []).map((p) => (
                                                            <PillarBar key={p.pillar} pillar={p} />
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Agent x Feature adoption matrix */}
            {data?.feature_matrix && agents.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Feature adoption</h3>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                            Which parts of the CRM each agent actually uses. Click any cell to see the
                            activity behind it. Green = used regularly, amber = used occasionally,
                            red = not used in this period.
                        </p>
                    </div>
                    <div className="overflow-x-auto">
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, ...stickyAgent, zIndex: 2, background: CLR.white, cursor: 'default' }}>
                                        Agent
                                    </th>
                                    {data.feature_matrix.modules.map((m) => (
                                        <th key={m.key} style={{ ...th, textAlign: 'center', cursor: 'default' }}>
                                            {m.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.feature_matrix.rows.map((r) => (
                                    <tr key={r.agent_id} className="hover:bg-gray-50">
                                        <td style={{ ...td, ...stickyAgent, background: CLR.white, fontWeight: 600 }}>
                                            {r.agent_name}
                                        </td>
                                        {r.cells.map((cell) => {
                                            const tier = cell.count >= 10 ? 'good' : cell.count > 0 ? 'mid' : 'bad';
                                            const { bg, fg } = pillPalette[tier];
                                            return (
                                                <td key={cell.module} style={{ ...td, textAlign: 'center' }}>
                                                    <button
                                                        disabled={!cell.count}
                                                        onClick={() => runDetail(
                                                            { agent: r.agent_id, metric: cell.module },
                                                            `${r.agent_name} · ${cell.module}`,
                                                        )}
                                                        title={cell.last_used
                                                            ? `${cell.count} actions · last used ${cell.last_used}`
                                                            : 'Not used in this period'}
                                                        style={{
                                                            background: bg, color: fg, border: 'none',
                                                            borderRadius: 6, padding: '3px 9px',
                                                            fontSize: 11, fontWeight: 700, minWidth: 38,
                                                            cursor: cell.count ? 'pointer' : 'default',
                                                        }}
                                                    >
                                                        {cell.count > 0 ? fmtNumber(cell.count) : '—'}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <CrmDetailModal
                {...detail}
                onClose={() => setDetail((d) => ({ ...d, open: false }))}
            />
        </div>
    );
};

export default AgentUsagePanel;
